
import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { DialogsService } from '../dialogs.service';
import { PwdChangeDirective } from './pwd-change.directive';
import { CompanyUser } from '../../core/user/companyUser.model';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { LocalizePipe } from '../../core/pipes/localize.pipe';
import { HttpService } from '../../core/http/http.service';
import { Utils } from '../../core/utils/utils';
import { NextMethodsEnum } from '../../core/enums/next-methods.enum';

@Component({
    selector: 'pwd-change-admin-dialog',
    templateUrl: './pwd-change-admin-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule, PwdChangeDirective, ReactiveFormsModule, TranslocoModule]
})
export class PwdChangeAdminDialog implements OnInit, OnDestroy {

    user: CompanyUser;
    newPassword: FormControl = new FormControl('', [Validators.required]);
    confirmPassword: FormControl = new FormControl('');
    formGroup: FormGroup;
    hideNewPassword: boolean = true;
    hideConfirmPassword: boolean = true;

    nChars: number = 0;
    nCharsValid: boolean;
    charMayus: boolean;
    charMinus: boolean;
    charNum: boolean;
    charSpecial: boolean;

    /**
     * Constructor
     */
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            user: CompanyUser;
        },
        public dialogRef: MatDialogRef<PwdChangeAdminDialog>,
        private fuseConfirmationService: FuseConfirmationService,
        private snackbar: MatSnackBar,
        private localize: LocalizePipe,
        private transloco: TranslocoService,
        private httpService: HttpService,
        private dialogsService: DialogsService) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.user=this.data.user;
        this.formGroup = new FormGroup({newPassword: this.newPassword, confirmPassword: this.confirmPassword});
    }

    /**
     * On destroy
     */
     ngOnDestroy(): void {
     }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    submitPassword() {
        if(!this.getErrorMessageNewPassword()){
            if(this.newPassword.value === this.confirmPassword.value){
                const confirmDialog = this.openConfirmDialog(this.transloco.translate('updatePassword'));
                confirmDialog.afterClosed().subscribe(async (confirm) => {
                    if (confirm === 'confirmed') {
                        await firstValueFrom(this.changePassword(this.user, this.newPassword.value).pipe(
                        catchError((error) => {
                            this.dialogsService.openErrorDialog({message: error?.error.echo && Object.keys(error.error.echo).length ? this.localize.transform(error.error.echo) : undefined});
                            return throwError(() => error);
                        })));
                        this.snackbar.open(this.transloco.translate('changePasswordSuccess'), this.transloco.translate('accept'), { duration: 5000 });
                        this.closeDialog();
                    }
                });
            }else{
                this.openWarningDialog(this.transloco.translate('passwordMustMatch'));
            }
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    getErrorMessageNewPassword() {
        if (this.newPassword.hasError('required')) {
            return this.transloco.translate('passwordIsRequired');
        }else if (this.newPassword.hasError('pattern')){
            return this.transloco.translate('invalidPassword');
        }else{
            return null;
        }
    }

    onChangeInput(event: any) {
        const newPassword = event.target.value;
        this.charMayus = false;
        this.charMinus = false;
        this.charNum = false;
        this.charSpecial = false;
        this.nChars = newPassword.length;
        this.nCharsValid = this.nChars > 9 && this.nChars < 16;
        if (newPassword !== '') {
            for (const char of newPassword) {
                if (Utils.isCharacterANumber(char)) {
                    this.charNum = true;
                } else if (Utils.isCharacterALetter(char)) {
                    if (char === char.toLowerCase()) {
                        this.charMinus = true;
                    } else {
                        this.charMayus = true;
                    }
                } else {
                    this.charSpecial = true;
                }
            }
        }
    }
        // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private openConfirmDialog(message: string) {
        return this.fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

    private openWarningDialog(message: string) {
        return this.fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel'),
                    show: false
                }
            }
        });
    }

    private changePassword(user: CompanyUser, password: string) {
        return this.httpService.post(NextMethodsEnum.changePassword, {userId: user.userId, newPassword: password});
    }
}
